"use client";

import Link from "next/link";
import Image from "next/image";
import { Phone, Mail, MapPin, Send, ArrowRight } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const contactMethods = [
  {
    icon: Phone,
    label: "Call Us",
    accent: "#EFAE33",
    accentLight: "#FEF6E4",
    lines: ["+234 703 096 9089", "+234 706 460 7741"],
    href: ["tel:+2347030969089", "tel:+2347064607741"],
    note: "Sun–Sat, 6 AM – 8 PM WAT",
  },
  {
    icon: Mail,
    label: "Email Us",
    accent: "#8253C3",
    accentLight: "#F5F3FE",
    lines: ["support@laplageservices.net"],
    href: ["mailto:support@laplageservices.net"],
    note: "Response within 24 hours",
  },
  {
    icon: MapPin,
    label: "Visit Us",
    accent: "#21205A",
    accentLight: "#EEEDFE",
    lines: [
      "2nd Floor, Room 201A1",
      "Novare Central, Wuse Zone-5",
      "Abuja, Nigeria",
    ],
    href: [
      "https://www.google.com/maps/place/Novare+Central/@9.0643295,7.4551771,17z",
    ],
    note: "Mon–Fri, 9 AM – 5 PM WAT",
  },
];

const telegramGroups = [
  {
    title: "Global Students Group",
    description:
      "Connect with thousands of students from across Africa and beyond. Share resources, ask questions, and grow together.",
    link: "https://t.me/laplagemetaverse",
    tag: "Open to all",
  },
  {
    title: "Volunteer Network",
    description:
      "Already a volunteer? Join the dedicated group to coordinate with fellow volunteers and stay updated on activities.",
    link: "https://t.me/+BwEWyiJc77AwYzlk",
    tag: "Volunteers only",
  },
];

export default function ContactUsPage() {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />

      {/* ── HERO ── */}
      <section className="relative bg-[#21205A] overflow-hidden">
        {/* subtle decorative rings */}
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full"
          style={{
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -top-16 -right-16 w-[300px] h-[300px] rounded-full"
          style={{ border: "1px solid rgba(255,255,255,0.08)" }}
        />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-0 min-h-[72vh] py-20 lg:py-0">
            {/* Text */}
            <div className="w-full lg:w-1/2 flex flex-col gap-6 z-10">
              <span
                className="inline-block text-sm font-semibold tracking-widest uppercase"
                style={{ color: "#EFAE33" }}
              >
                Get in Touch
              </span>

              <h1 className="font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.1]">
                We're always{" "}
                <span
                  style={{
                    backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  here for you.
                </span>
              </h1>

              <p className="text-white/70 text-lg lg:text-xl leading-relaxed max-w-lg">
                Whether you have a question about courses, partnerships, or
                anything else — our team is ready to answer all your questions.
              </p>

              <div className="flex items-center gap-3 mt-2">
                <span className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: "#4ade80" }}
                  />
                  <span className="text-white/60 text-sm">
                    Available 24 / 7
                  </span>
                </span>
              </div>
            </div>

            {/* Image */}
            <div className="w-full lg:w-1/2 lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2">
              <div className="relative w-full aspect-[4/3] max-w-xl mx-auto">
                <Image
                  src="/contact-us.jpg"
                  alt="Contact La Plage Meta Verse"
                  fill
                  priority
                  className="object-contain object-center lg:object-right"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
            </div>
          </div>
        </div>

        {/* diagonal cut */}
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-0 w-full overflow-hidden leading-none"
          style={{ height: 60 }}
        >
          <svg
            viewBox="0 0 1440 60"
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            <polygon points="0,60 1440,0 1440,60" fill="white" />
          </svg>
        </div>
      </section>

      {/* ── CONTACT METHOD CARDS ── */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-bold text-[#21205A] text-3xl sm:text-4xl lg:text-5xl">
              Reach us your way
            </h2>
            <p className="mt-4 text-gray-500 text-lg max-w-xl mx-auto">
              Pick whichever channel works best for you. Every message lands
              with a real person.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contactMethods.map((method) => {
              const Icon = method.icon;
              return (
                <div
                  key={method.label}
                  className="group flex flex-col gap-5 rounded-2xl border border-gray-100 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  style={{ background: "#FAFAFA" }}
                >
                  {/* icon badge */}
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: method.accentLight }}
                  >
                    <Icon
                      className="w-7 h-7"
                      style={{ color: method.accent }}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <p className="font-bold text-[#21205A] text-xl">
                      {method.label}
                    </p>
                    <p className="text-sm text-gray-400">{method.note}</p>
                  </div>

                  <div className="flex flex-col gap-2 mt-auto">
                    {method.lines.map((line, i) => (
                      <Link
                        key={line}
                        href={method.href[Math.min(i, method.href.length - 1)]}
                        target={
                          method.href[0].startsWith("http")
                            ? "_blank"
                            : undefined
                        }
                        rel={
                          method.href[0].startsWith("http")
                            ? "noopener noreferrer"
                            : undefined
                        }
                        className="font-medium text-base transition-colors hover:opacity-80"
                        style={{ color: method.accent }}
                      >
                        {line}
                      </Link>
                    ))}
                  </div>

                  {method.href[0].startsWith("http") && (
                    <Link
                      href={method.href[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold mt-1 transition-opacity hover:opacity-70"
                      style={{ color: method.accent }}
                    >
                      Open in Maps
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TELEGRAM ── */}
      <section
        className="py-20 lg:py-28 relative overflow-hidden"
        style={{ background: "#21205A" }}
      >
        {/* gold accent bar */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-0 w-full h-1"
          style={{
            backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
          }}
        />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
            {/* left: heading */}
            <div className="lg:w-2/5 flex flex-col gap-5 lg:sticky lg:top-28">
              <span
                className="inline-flex items-center gap-2 text-sm font-semibold tracking-widest uppercase"
                style={{ color: "#EFAE33" }}
              >
                <Send className="w-4 h-4" />
                Telegram Community
              </span>

              <h2 className="font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Join thousands learning together.
              </h2>

              <p className="text-white/60 text-lg leading-relaxed">
                Our Telegram groups are where the learning community stays
                connected — get updates, ask questions, and meet peers from
                across the world.
              </p>
            </div>

            {/* right: cards */}
            <div className="lg:w-3/5 flex flex-col gap-5">
              {telegramGroups.map((group) => (
                <div
                  key={group.title}
                  className="rounded-2xl p-7 flex flex-col sm:flex-row sm:items-center gap-6 transition-all duration-300 hover:opacity-90"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  {/* telegram icon */}
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(239,174,51,0.15)" }}
                  >
                    <Send className="w-6 h-6" style={{ color: "#EFAE33" }} />
                  </div>

                  <div className="flex-1 flex flex-col gap-1.5">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="font-bold text-white text-lg">
                        {group.title}
                      </p>
                      <span
                        className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
                        style={{
                          background: "rgba(239,174,51,0.2)",
                          color: "#EFAE33",
                        }}
                      >
                        {group.tag}
                      </span>
                    </div>
                    <p className="text-white/60 text-sm leading-relaxed">
                      {group.description}
                    </p>
                  </div>

                  <Link
                    href={group.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors flex-shrink-0"
                    style={{
                      background: "#EFAE33",
                      color: "#21205A",
                    }}
                  >
                    Join
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA STRIP ── */}
      <section className="py-16 bg-[#F5F3FE]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center gap-6">
          <h2 className="font-bold text-[#21205A] text-2xl sm:text-3xl lg:text-4xl max-w-2xl leading-tight">
            Not sure where to start?{" "}
            <span
              style={{
                backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Explore our courses.
            </span>
          </h2>
          <p className="text-gray-500 text-lg max-w-lg">
            Browse Africa's leading synchronous learning platform and find a
            programme that fits your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              href="https://student.laplagemetaverse.org/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-bold text-white transition-opacity hover:opacity-90"
              style={{ background: "#8253C3" }}
            >
              Create an Account
            </Link>
            <Link
              href="https://student.laplagemetaverse.org/sign-in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-bold border-2 transition-colors bg-[#21205A] text-[#FFF] hover:bg-[#21205A]/90"
              style={{ borderColor: "#21205A", color: "" }}
            >
              View Courses
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
